/**
 * Touren: die Google-Maps-Links einer Tour mit der Angabe, welche Halte jeder Teil abdeckt.
 * Reine Logik ohne React, damit `node --test` sie lädt. Planerseite und Fahreransicht nutzen dieselbe Datei.
 * Die Teile und ihre Reihenfolge kommen von `tourLinks` (tourLogic.ts); hier kommen nur die Haltnummern dazu.
 */
import { MAX_WAYPOINTS, hasCoords, tourLinks, type MaybeCoords, type TourLinkOptions } from './tourLogic.ts'

export interface LinkPart {
  href: string
  /** 1-basierte Nummer des ersten und letzten Halts im Teil (Nummer in der Tour, auch wenn Halte ohne Koordinaten davor stehen). null: der Teil führt nur zurück zum Start. */
  from: number | null
  to: number | null
  /** true: der Teil endet am Start (Rückfahrt). */
  toStart: boolean
}

export interface TourLinkSet {
  parts: LinkPart[]
  /** Halte ohne Koordinaten. Sie fehlen in den Links. */
  missing: number
}

/** `stops` sind die Haltepunkte in Tour-Reihenfolge (lat/lon dürfen fehlen). */
export function linkParts(start: MaybeCoords | null | undefined, stops: MaybeCoords[], returnToStart: boolean, opts: TourLinkOptions = {}): TourLinkSet {
  const located: number[] = []
  stops.forEach((s, i) => { if (hasCoords(s)) located.push(i + 1) })
  const missing = stops.length - located.length
  const hrefs = tourLinks(start, stops, returnToStart, opts)
  const withReturn = returnToStart && hasCoords(start) && located.length > 0
  const total = located.length + (withReturn ? 1 : 0)
  const size = MAX_WAYPOINTS + 1
  const parts: LinkPart[] = hrefs.map((href, n) => {
    const first = n * size
    const last = Math.min(first + size, total) - 1
    const stopsHere = located.slice(first, last + 1)
    return {
      href,
      from: stopsHere.length ? stopsHere[0] : null,
      to: stopsHere.length ? stopsHere[stopsHere.length - 1] : null,
      toStart: withReturn && last === total - 1,
    }
  })
  return { parts, missing }
}

/** Zeile unter "Teil 1/2", zum Beispiel "Halt 1–4" oder "Halt 5–6 und zurück". */
export function partRange(part: LinkPart): string {
  const stops = part.from == null || part.to == null ? '' : part.from === part.to ? `Halt ${part.from}` : `Halt ${part.from}–${part.to}`
  if (!part.toStart) return stops
  return stops ? `${stops} und zurück` : 'Zurück zum Start'
}
